import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import TagManager from 'react-gtm-module';
import type mapboxgl from 'mapbox-gl';
import { BUILDING_DOTS_MIN_ZOOM } from '../../../HouseMap/BuildingMap';
import {
  HintPlacement,
  placeMapHint,
  mapHintLayoutKey,
  TARGET_GAP,
} from './mapHintPlacement';
import tail from './assets/hint-tail.svg';
import close from './assets/hint-close.svg';
import './MapOnboardingHint.scss';
import { localStore } from '../../../utils/safeStorage';

export const MAP_ONBOARDING_KEY = 'jaakko:map-onboarding:v1';
const EXPLORED_DELAY = 4000;
const IDLE_DELAY = 10000;
const LOADING_GRACE = 4000;

type DebugReason =
  | 'disabled'
  | 'blocked'
  | 'hidden'
  | 'cookie'
  | 'pointer'
  | 'moving'
  | 'loading'
  | 'settling'
  | 'waiting'
  | 'seen'
  | 'no-placement'
  | 'shown';

interface DebugState {
  reason: DebugReason;
  elapsed: number;
  quiet: number;
  loading: number;
  explored: boolean;
  shown: boolean;
}

type DebugWindow = Window & {
  __mapOnboarding?: DebugState;
};

interface Props {
  map: mapboxgl.Map | null;
  enabled: boolean;
  isLoggedIn: boolean;
  hasCheckedLogin: boolean;
  blocked: boolean;
  /** Quiet map time before presenting or resuming guidance after other UI. */
  settleDelayMs?: number;
  /** Changes when search explicitly moves to a new place. */
  locationRevision?: number;
}

/**
 * Suppress a lesson already shown in this browser. If storage cannot be read,
 * skip the hint rather than risk showing a previously seen lesson again. Uses
 * the throwing read: the plain one reports unreadable storage as "unseen".
 */
function wasSeen() {
  try {
    return !!localStore.getItemOrThrow(MAP_ONBOARDING_KEY);
  } catch {
    return true;
  }
}

/**
 * Persist the lesson's first impression or outcome across browser sessions.
 * Return false if storage is unavailable so an unrecordable hint can be skipped.
 * Uses the throwing write: the plain one falls back to memory, which a reload loses.
 */
function remember(value: string): boolean {
  try {
    localStore.setItemOrThrow(MAP_ONBOARDING_KEY, value);
    return true;
  } catch {
    return false;
  }
}

/** Report an onboarding action with its zoom-prompt or building-prompt state. */
function track(action: string, near: boolean) {
  TagManager.dataLayer({
    dataLayer: {
      event: 'map_building_hint',
      action,
      state: near ? 'building' : 'zoom',
    },
  });
}

/**
 * Detect visible GetTerms UI outside React's modal state. Map marker popups are
 * intentionally allowed alongside onboarding and must not pause the lesson.
 */
function hasVisibleCookieUi() {
  const cookieRoot = document.querySelector(
    'cookie-widget-wrapper',
  )?.shadowRoot;
  return !!cookieRoot?.querySelector(
    '[data-gt-cookie-widget-shown="true"], [data-gt-cookie-dialog-shown="true"]',
  );
}

/**
 * Teach logged-out visitors once per browser after the browsing CTA. Count only
 * active, unblocked browsing time; wait for a settled map and a suitable target.
 * Persist the first display and clean up map, document, and timer listeners.
 */
export default function MapOnboardingHint({
  map,
  enabled: browsingEnabled,
  isLoggedIn,
  hasCheckedLogin,
  blocked,
  settleDelayMs = 0,
  locationRevision = 0,
}: Props) {
  const enabled = browsingEnabled && hasCheckedLogin && !isLoggedIn;
  const { t } = useTranslation();
  const titleId = useId();
  const [view, setView] = useState<{
    near: boolean;
    placement: HintPlacement;
  } | null>(null);
  // Keep the polling effect alive when a saved flag exists so clearing that
  // flag in local development can restart the lesson without a page reload.
  const [retired, setRetired] = useState(false);
  const [touch, setTouch] = useState(
    () => window.matchMedia('(pointer: coarse)').matches,
  );
  const cardRef = useRef<HTMLDivElement>(null);
  const heightRef = useRef(180);
  const viewRef = useRef(view);
  const gates = useRef({ enabled, blocked, settleDelayMs });
  useLayoutEffect(() => {
    viewRef.current = view;
    gates.current = { enabled, blocked, settleDelayMs };
  }, [view, enabled, blocked, settleDelayMs]);
  const journey = useRef({
    elapsed: 0,
    quiet: 0,
    loading: 0,
    explored: false,
    shown: false,
    near: false,
    awaitingBuilding: locationRevision > 0,
  });
  const previousLocationRevision = useRef(locationRevision);

  useLayoutEffect(() => {
    if (previousLocationRevision.current === locationRevision) return;
    previousLocationRevision.current = locationRevision;
    // A searched destination can be open country. Do not restart the generic
    // zoom lesson there; quietly wait for an actual clickable footprint.
    journey.current.awaitingBuilding = true;
    journey.current.elapsed = 0;
    journey.current.quiet = 0;
    journey.current.loading = 0;
    setView(null);
  }, [locationRevision]);

  const dismiss = useCallback((reason: string) => {
    remember('dismissed');
    track(reason, !!viewRef.current?.near);
    setRetired(true);
    setView(null);
  }, []);

  useLayoutEffect(() => {
    if (!enabled || blocked) {
      // Retain lesson progress, but never flash an old placement when another
      // surface closes. The next settled tick chooses the current zoom phase.
      journey.current.quiet = 0;
      setView(null);
    }
  }, [enabled, blocked]);

  useEffect(() => {
    if (!isLoggedIn) return;
    setView(null);
    // Signing back out must not restart an impression already consumed.
    if (journey.current.shown) setRetired(true);
  }, [isLoggedIn]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === MAP_ONBOARDING_KEY && event.newValue) {
        setRetired(true);
        setView(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const query = window.matchMedia('(pointer: coarse)');
    const update = () => setTouch(query.matches);
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', update);
      return () => query.removeEventListener('change', update);
    }
    // Older Safari exposes MediaQueryList's original listener API.
    query.addListener(update);
    return () => query.removeListener(update);
  }, []);

  useEffect(() => {
    if (!view || blocked || !enabled || retired || !cardRef.current) return;
    const observer = new ResizeObserver(() => {
      heightRef.current =
        cardRef.current?.getBoundingClientRect().height || 180;
    });
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [view, blocked, enabled, retired]);

  useEffect(() => {
    if (!map || retired) return;
    const progress = journey.current;
    let lastTick = Date.now();
    let removed = false;
    let pointerDown = false;
    let placementKey = '';
    let emptyBuildingPlacementKey = '';
    let lastDebugReason: DebugReason | null = null;
    const report = (reason: DebugReason) => {
      if (process.env.NODE_ENV === 'production') return;
      const state: DebugState = {
        reason,
        elapsed: progress.elapsed,
        quiet: progress.quiet,
        loading: progress.loading,
        explored: progress.explored,
        shown: progress.shown,
      };
      (window as DebugWindow).__mapOnboarding = state;
      if (
        process.env.NODE_ENV === 'development' &&
        reason !== lastDebugReason
      ) {
        console.debug('[MapOnboarding]', reason, state);
      }
      lastDebugReason = reason;
    };
    const hide = () => setView(null);
    const moveStart = (event: { originalEvent?: Event }) => {
      progress.quiet = 0;
      progress.loading = 0;
      const userMovement = !!event.originalEvent || pointerDown;
      if (gates.current.enabled && userMovement) progress.explored = true;
      // A building pointer belongs to one viewport. Do not keep pointing at
      // an old footprint while the visitor pans into an empty area.
      if (viewRef.current?.near) hide();
    };
    const moveEnd = () => {
      progress.quiet = 0;
      progress.loading = 0;
      // Re-anchor a visible hint to the newly settled viewport. Keep it visible
      // while panning so map interaction cannot accidentally dismiss the lesson.
      placementKey = '';
      emptyBuildingPlacementKey = '';
    };
    const pointerStart = () => {
      pointerDown = true;
      progress.quiet = 0;
      progress.loading = 0;
    };
    const pointerEnd = () => {
      pointerDown = false;
    };
    const visibility = () => {
      lastTick = Date.now();
      progress.quiet = 0;
    };
    const resize = () => {
      progress.quiet = 0;
      // Mobile browser chrome and orientation changes can emit resize events
      // without user intent. Preserve the card and re-anchor it once settled.
      placementKey = '';
      emptyBuildingPlacementKey = '';
    };
    const sourceData = (event: { sourceId?: string; isSourceLoaded?: boolean }) => {
      const buildingSource = map.getLayer('building')?.source;
      if (event.isSourceLoaded && event.sourceId === buildingSource) {
        emptyBuildingPlacementKey = '';
      }
    };
    const remove = () => {
      removed = true;
      hide();
    };
    const canvas = map.getCanvas();
    canvas.addEventListener('pointerdown', pointerStart);
    window.addEventListener('pointerup', pointerEnd);
    window.addEventListener('pointercancel', pointerEnd);
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', visibility);
    map.on('movestart', moveStart);
    map.on('moveend', moveEnd);
    map.on('remove', remove);
    map.on('sourcedata', sourceData);

    const tick = () => {
      const now = Date.now();
      // Clamp suspended/background timer gaps; they are not active reading time.
      const delta = Math.min(now - lastTick, 500);
      lastTick = now;
      if (removed) return;
      if (!gates.current.enabled) {
        progress.quiet = 0;
        report('disabled');
        return;
      }
      if (gates.current.blocked) {
        progress.quiet = 0;
        report('blocked');
        return;
      }
      if (document.hidden) {
        progress.quiet = 0;
        report('hidden');
        return;
      }
      if (hasVisibleCookieUi()) {
        progress.quiet = 0;
        report('cookie');
        return;
      }
      progress.elapsed += delta;
      if (pointerDown) {
        progress.quiet = 0;
        progress.loading = 0;
        report('pointer');
        return;
      }
      if (map.isMoving()) {
        progress.quiet = 0;
        progress.loading = 0;
        report('moving');
        return;
      }
      progress.quiet += delta;
      if (progress.quiet < gates.current.settleDelayMs) {
        report('settling');
        return;
      }
      if (
        !progress.shown &&
        progress.elapsed < (progress.explored ? EXPLORED_DELAY : IDLE_DELAY)
      ) {
        report('waiting');
        return;
      }
      // Another already-open tab may have displayed the lesson since mount.
      if (!progress.shown && wasSeen()) {
        report('seen');
        return;
      }
      const near = map.getZoom() >= BUILDING_DOTS_MIN_ZOOM;
      if (!near && (progress.awaitingBuilding || progress.near)) {
        hide();
        report('no-placement');
        return;
      }
      // Never retain a zoom prompt at building zoom (or a stale building
      // pointer after zooming out) while waiting for the next valid target.
      if (viewRef.current && viewRef.current.near !== near) hide();
      const mapLoaded = map.isStyleLoaded() && map.areTilesLoaded();
      if (!mapLoaded) {
        progress.loading += delta;
        // The zoom prompt does not query rendered map data, so a permanently
        // busy source must not block it forever. Building targeting still waits
        // for a fully loaded style and tiles.
        if (near || progress.loading < LOADING_GRACE) {
          report('loading');
          return;
        }
      } else {
        progress.loading = 0;
      }
      const nextPlacementKey = `${near}:${heightRef.current}:${mapHintLayoutKey(map)}`;
      if (near && emptyBuildingPlacementKey === nextPlacementKey) {
        report('no-placement');
        return;
      }
      if (viewRef.current && placementKey === nextPlacementKey) {
        report('shown');
        return;
      }
      const placement = placeMapHint(map, near, heightRef.current);
      if (!placement) {
        // A settled, empty building viewport is a waiting state. Retry only
        // after its camera/layout or building data changes, without auto-zoom.
        if (near) {
          emptyBuildingPlacementKey = nextPlacementKey;
          hide();
        }
        placementKey = '';
        report('no-placement');
        return;
      }
      placementKey = nextPlacementKey;
      emptyBuildingPlacementKey = '';
      progress.near ||= near;
      progress.awaitingBuilding = false;
      if (!progress.shown) {
        if (!remember('shown')) {
          setRetired(true);
          return;
        }
        progress.shown = true;
        track('shown', near);
      }
      const previous = viewRef.current;
      if (
        !previous ||
        previous.near !== near ||
        JSON.stringify(previous.placement) !== JSON.stringify(placement)
      ) {
        setView({ near, placement });
      }
      report('shown');
    };
    const timer = window.setInterval(tick, 250);
    return () => {
      window.clearInterval(timer);
      canvas.removeEventListener('pointerdown', pointerStart);
      window.removeEventListener('pointerup', pointerEnd);
      window.removeEventListener('pointercancel', pointerEnd);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', visibility);
      map.off('movestart', moveStart);
      map.off('moveend', moveEnd);
      map.off('remove', remove);
      map.off('sourcedata', sourceData);
      if (process.env.NODE_ENV !== 'production') {
        delete (window as DebugWindow).__mapOnboarding;
      }
    };
  }, [map, retired]);

  useEffect(() => {
    if (!view || blocked || !enabled || retired) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss('closed');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [view, blocked, enabled, retired, dismiss]);

  const zoomIn = () => {
    if (!map || !view) return;
    const progress = journey.current;
    progress.quiet = 0;
    progress.awaitingBuilding = true;
    track('zoom_in', false);
    const rect = map.getContainer().getBoundingClientRect();
    // Keep the area under the arrow in place, even beside the desktop sidebar.
    const around = map.unproject([
      view.placement.x + view.placement.arrowX - rect.left,
      view.placement.y + TARGET_GAP - rect.top,
    ]);
    setView(null);
    map.easeTo({
      zoom: Math.max(map.getZoom(), BUILDING_DOTS_MIN_ZOOM + 0.5),
      around,
      duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 0
        : 650,
    });
    map.getCanvas().focus({ preventScroll: true });
  };

  const visible = !!view && enabled && !blocked && !retired;
  const near = view?.near ?? false;
  const placement = view?.placement;
  const prefix = 'newLandingPage.mapOnboarding';
  const title = t(
    `${prefix}.${near ? (touch ? 'nearTitleTouch' : 'nearTitle') : 'farTitle'}`,
  );
  const body = t(
    `${prefix}.${near ? 'nearBody' : touch ? 'farBodyTouch' : 'farBody'}`,
  );
  return createPortal(
    <>
      <span
        className="map-onboarding-announcement"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {visible ? `${title}. ${body}` : ''}
      </span>
      {visible && (
        <>
          {placement.target && (
            <span
              className="map-onboarding-target"
              aria-hidden="true"
              style={{ left: placement.target.x, top: placement.target.y }}
            />
          )}
          <div
            ref={cardRef}
            className="map-onboarding-hint"
            role="region"
            aria-labelledby={titleId}
            data-state={near ? 'building' : 'zoom'}
            style={
              {
                left: placement.x,
                top: placement.y,
                width: placement.width,
                '--hint-arrow-x': `${placement.arrowX}px`,
              } as React.CSSProperties
            }
          >
            <img
              className="map-onboarding-hint__avatar"
              src={`${process.env.PUBLIC_URL}/assets/jaakko_avatar.png`}
              alt=""
            />
            <div className="map-onboarding-hint__content">
              <div>
                <p id={titleId} className="map-onboarding-hint__title">
                  {title}
                </p>
                <p>{body}</p>
              </div>
              <button
                className="map-onboarding-hint__action"
                type="button"
                onClick={near ? () => dismiss('got_it') : zoomIn}
              >
                <span>{t(`${prefix}.${near ? 'gotIt' : 'zoomIn'}`)}</span>
              </button>
            </div>
            <button
              className="map-onboarding-hint__close"
              type="button"
              aria-label={t(`${prefix}.dismiss`)}
              onClick={() => dismiss('closed')}
            >
              <img src={close} alt="" />
            </button>
            <img className="map-onboarding-hint__tail" src={tail} alt="" />
          </div>
        </>
      )}
    </>,
    document.body,
  );
}
